import { FREE_GIFT_IDS, MAX_QTY } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftInteraction } from '@/components/blocks/FreeGiftV2/context/FreeGiftInteractionContext';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { ProductDetails } from '@/components/blocks/FreeGiftV2/parts/ProductDetails';
import { DrawerContentSx, DrawerPurchaseSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import { SkuQuantity } from '@/components/blocks/FreeGiftV2/types';
import {
	getCombinableAchievableQty,
	getCombinableMessageKind,
	getDeficientSkus,
	getFixedGroupMessageKind,
	isFixedPurchaseGroupOutOfStock,
	isOutOfStock,
} from '@/components/blocks/FreeGiftV2/utils';
import { NumberInput } from '@/components/blocks/NumberInput';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Alert, Box, Checkbox, Chip, Radio, Typography, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

export const DrawerPurchase = () => {
	const labels = useLocalization('FreeGift');
	const theme = useTheme();
	const { currentPartNumber, purchase, purchaseQty, othersQty, marketInventory } =
		useFreeGiftPromotion();
	const {
		openDrawer,
		promotionQty,
		setPromotionQty,
		selectedPurchaseGroupId,
		setSelectedPurchaseGroupId,
		selectedPurchaseSkus,
		setSelectedPurchaseSkus,
	} = useFreeGiftInteraction();
	const isCombinable = purchase?.combinable;
	const totalQuantity = (purchaseQty + othersQty) * promotionQty;
	const groups = purchase?.purchases ?? [];

	const currentProductFirst = (skus: SkuQuantity[]) =>
		[...skus].sort(
			(a, b) => Number(b.sku === currentPartNumber) - Number(a.sku === currentPartNumber)
		);

	const parentPurchase = purchase?.purchases?.[0]?.triggerSKUs?.find(
		(sku) => sku.sku === currentPartNumber
	);

	const previousPromotionQtyRef = useRef(promotionQty);

	useEffect(() => {
		if (isCombinable && promotionQty < previousPromotionQtyRef.current) {
			setSelectedPurchaseSkus(parentPurchase ? [{ sku: parentPurchase.sku, quantity: 1 }] : []);
		}

		previousPromotionQtyRef.current = promotionQty;
	}, [isCombinable, promotionQty, parentPurchase, setSelectedPurchaseSkus]);

	useEffect(() => {
		if (isCombinable) {
			return;
		}

		const existingSelection = groups.find(
			(group) => group.triggerGroupId === selectedPurchaseGroupId
		);

		const fallbackGroup =
			!existingSelection &&
			groups.length === 1 &&
			!isFixedPurchaseGroupOutOfStock(groups[0], promotionQty, marketInventory)
				? groups[0]
				: undefined;
		const selectedGroup = existingSelection ?? fallbackGroup;

		if (!selectedGroup) {
			return;
		}

		if (selectedPurchaseGroupId !== selectedGroup.triggerGroupId) {
			setSelectedPurchaseGroupId(selectedGroup.triggerGroupId);
		}

		setSelectedPurchaseSkus(
			selectedGroup.triggerSKUs.map((sku) => ({ ...sku, quantity: sku.quantity * promotionQty }))
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, groups, selectedPurchaseGroupId, promotionQty, marketInventory]);

	const combinableCandidates = purchase?.purchases?.[0]?.triggerSKUs ?? [];
	const hasSingleCombinableOption = combinableCandidates.length === 1;
	const isParentOnlyCandidate =
		hasSingleCombinableOption && combinableCandidates[0]?.sku === currentPartNumber;

	useEffect(() => {
		if (!isCombinable || !parentPurchase) {
			return;
		}

		if (isParentOnlyCandidate) {
			setSelectedPurchaseSkus([{ sku: parentPurchase.sku, quantity: totalQuantity }]);
			return;
		}

		setSelectedPurchaseSkus((current) => {
			const hasParentSelected = current.some((item) => item.sku === parentPurchase.sku);

			return hasParentSelected ? current : [...current, { sku: parentPurchase.sku, quantity: 1 }];
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, parentPurchase, isParentOnlyCandidate, totalQuantity]);

	useEffect(() => {
		if (!isCombinable || !hasSingleCombinableOption) {
			return;
		}

		const firstCandidate = combinableCandidates.find(
			(sku) => sku.sku !== currentPartNumber && !isOutOfStock(marketInventory[sku.sku])
		);

		if (!firstCandidate) {
			return;
		}

		setSelectedPurchaseSkus((current) => {
			const parentEntry = current.find((item) => item.sku === currentPartNumber);
			const remaining = Math.max(1, totalQuantity - (parentEntry?.quantity ?? 0));

			return parentEntry
				? [parentEntry, { sku: firstCandidate.sku, quantity: remaining }]
				: [{ sku: firstCandidate.sku, quantity: remaining }];
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCombinable, hasSingleCombinableOption, currentPartNumber, totalQuantity, marketInventory]);

	const selectSingleTriggerCandidate = (sku: string) => {
		setSelectedPurchaseSkus((current) => {
			const parentEntry = current.find((item) => item.sku === currentPartNumber);
			const remaining = Math.max(1, totalQuantity - (parentEntry?.quantity ?? 0));

			return parentEntry
				? [parentEntry, { sku, quantity: remaining }]
				: [{ sku, quantity: remaining }];
		});
	};

	const wasDrawerOpenRef = useRef(openDrawer);

	useEffect(() => {
		const wasOpen = wasDrawerOpenRef.current;
		wasDrawerOpenRef.current = openDrawer;

		if (wasOpen || !openDrawer) {
			return;
		}

		if (isCombinable) {
			setSelectedPurchaseSkus((current) =>
				current.filter((item) => item.sku === currentPartNumber || marketInventory[item.sku] !== 0)
			);
			return;
		}

		const selectedGroup = groups.find((group) => group.triggerGroupId === selectedPurchaseGroupId);

		if (
			selectedGroup &&
			isFixedPurchaseGroupOutOfStock(selectedGroup, promotionQty, marketInventory)
		) {
			setSelectedPurchaseGroupId(undefined);
			setSelectedPurchaseSkus([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openDrawer]);

	const promotionQtyField = (
		<Box sx={DrawerPurchaseSx.promotionField}>
			<Typography variant="bodySubText" color="grey.300" width="100%">
				{labels.PromotionQty.t()}
			</Typography>
			<NumberInput
				onChange={(qty) => setPromotionQty(qty ?? 0)}
				value={promotionQty}
				min={1}
				max={MAX_QTY}
				showControls
				disallowEmptyOnBlur
				id={FREE_GIFT_IDS.promotionQuantity}
				sx={DrawerPurchaseSx.field}
				maxLength={4}
			/>
		</Box>
	);

	const purchaseMessageKind = isCombinable
		? (() => {
				const candidates = groups[0]?.triggerSKUs ?? [];
				const hasParentCandidate = candidates.some((sku) => sku.sku === currentPartNumber);
				const nonParentCandidates = candidates.filter((sku) => sku.sku !== currentPartNumber);
				const isDeficient =
					!hasParentCandidate &&
					getCombinableAchievableQty(nonParentCandidates, totalQuantity, marketInventory) <
						totalQuantity;

				return {
					kind: getCombinableMessageKind(candidates.length, isDeficient),
					sku: candidates[0]?.sku,
				};
		  })()
		: (() => {
				const selectedGroup =
					groups.find((group) => group.triggerGroupId === selectedPurchaseGroupId) ??
					(groups.length === 1 ? groups[0] : undefined);

				if (!selectedGroup) {
					return { kind: 'none' as const, sku: undefined };
				}

				const deficientSkus = getDeficientSkus(
					selectedGroup.triggerSKUs,
					promotionQty,
					marketInventory
				);

				return { kind: getFixedGroupMessageKind(deficientSkus), sku: deficientSkus[0]?.sku };
		  })();

	const purchaseMessageAlert =
		purchaseMessageKind.kind !== 'none' ? (
			<Alert
				severity="error"
				icon={
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.cancel}
						color={theme.palette.error.dark}
						size="24px"
					/>
				}
				sx={DrawerContentSx.insufficientItemsAlert}
			>
				{purchaseMessageKind.kind === 'sku'
					? labels.InsufficientItemsForSku.t({ sku: purchaseMessageKind.sku })
					: labels.InsufficientItemsForPromotion.t()}
			</Alert>
		) : null;

	if (!isCombinable) {
		return (
			<>
				{promotionQtyField}
				{purchaseMessageAlert}
				{purchase?.purchases?.map((group) => {
					const isSelected = group.triggerGroupId === selectedPurchaseGroupId;
					const isSingleSelfItem =
						groups.length === 1 &&
						group.triggerSKUs.length === 1 &&
						group.triggerSKUs[0].sku === currentPartNumber;
					const groupOutOfStock = isFixedPurchaseGroupOutOfStock(
						group,
						promotionQty,
						marketInventory
					);

					return (
						<Box
							key={group.triggerGroupId}
							sx={isSelected ? DrawerPurchaseSx.groupSelected : DrawerPurchaseSx.group}
						>
							{groupOutOfStock ? <Box sx={DrawerPurchaseSx.disableOverlay} /> : null}
							{isSingleSelfItem ? (
								<Chip
									label={labels.ParentProduct.t()}
									size="small"
									sx={DrawerPurchaseSx.parentProductChip}
								/>
							) : null}
							<Box display="flex">
								<Radio
									checked={isSelected}
									onChange={() => setSelectedPurchaseGroupId(group.triggerGroupId)}
									disabled={groupOutOfStock}
									disableFocusRipple
									disableRipple
									sx={DrawerPurchaseSx.fields}
								/>
								<Box sx={DrawerPurchaseSx.productDetails}>
									{currentProductFirst(group.triggerSKUs).map((sku: SkuQuantity) => (
										<ProductDetails
											key={sku.sku}
											sku={sku.sku}
											isParentProduct={isSingleSelfItem ? false : sku.sku === currentPartNumber}
											quantity={sku.quantity * promotionQty}
											isPurchaseView
											selected={isSelected}
											outOfStock={
												sku.sku !== currentPartNumber && isOutOfStock(marketInventory[sku.sku])
											}
										/>
									))}
								</Box>
							</Box>
						</Box>
					);
				})}
			</>
		);
	}

	const purchasedProducts = currentProductFirst(purchase?.purchases?.[0]?.triggerSKUs ?? []);
	const totalSelectedQty = selectedPurchaseSkus.reduce((sum, item) => sum + item.quantity, 0);

	const updateSkuQuantity = (sku: string, quantity: number) => {
		const others = selectedPurchaseSkus.filter((item) => item.sku !== sku);
		const othersTotal = others.reduce((sum, item) => sum + item.quantity, 0);

		const availableQty = sku === currentPartNumber ? Infinity : marketInventory[sku] ?? Infinity;
		const clampedQuantity = Math.max(
			0,
			Math.min(quantity, totalQuantity - othersTotal, availableQty)
		);

		setSelectedPurchaseSkus(
			clampedQuantity > 0 ? [...others, { sku, quantity: clampedQuantity }] : others
		);
	};

	const handleToggle = (sku: string, isParentProduct: boolean, checked: boolean) => {
		if (!checked) {
			updateSkuQuantity(sku, 0);
			return;
		}

		updateSkuQuantity(sku, isParentProduct ? 1 : Math.min(1, totalQuantity - totalSelectedQty));
	};

	return (
		<>
			{promotionQtyField}
			{purchaseMessageAlert}
			<Typography variant="bodyBaseline" color="grey.500">
				{labels.BuyNProductsToUnlockGifts.t({ n: totalQuantity })}
			</Typography>
			{purchasedProducts.map((sku: SkuQuantity) => {
				const isParentProduct = sku.sku === currentPartNumber;
				const currentQty = selectedPurchaseSkus.find((item) => item.sku === sku.sku)?.quantity ?? 0;
				const isSelected = isParentProduct || currentQty > 0;
				const remainingForThisItem = totalQuantity - totalSelectedQty + currentQty;
				const availableQty = isParentProduct ? undefined : marketInventory[sku.sku];
				const outOfStock = !isParentProduct && isOutOfStock(availableQty);
				const isQuotaLocked =
					!hasSingleCombinableOption &&
					!isParentProduct &&
					!isSelected &&
					totalSelectedQty >= totalQuantity;

				return (
					<Box
						key={sku.sku}
						sx={
							isSelected
								? DrawerPurchaseSx.combinableGroupSelected
								: DrawerPurchaseSx.combinableGroup
						}
					>
						{isQuotaLocked || (hasSingleCombinableOption && outOfStock) ? (
							<Box sx={DrawerPurchaseSx.disableOverlay} />
						) : null}
						{isParentProduct ? (
							<Chip
								label={labels.ParentProduct.t()}
								size="small"
								sx={DrawerPurchaseSx.parentProductChip}
							/>
						) : null}
						<Box display="flex">
							{hasSingleCombinableOption ? (
								<Radio
									checked={isSelected}
									disabled={isParentProduct || outOfStock}
									onChange={() => selectSingleTriggerCandidate(sku.sku)}
									disableFocusRipple
									disableRipple
									sx={DrawerPurchaseSx.fields}
								/>
							) : (
								<Checkbox
									checked={isSelected}
									disabled={
										isParentProduct ||
										outOfStock ||
										(!isSelected && totalSelectedQty >= totalQuantity)
									}
									onChange={(e) => handleToggle(sku.sku, isParentProduct, e.target.checked)}
									disableFocusRipple
									disableRipple
									disableTouchRipple
									sx={DrawerPurchaseSx.checkboxFields}
								/>
							)}
							<ProductDetails
								sku={sku.sku}
								isParentProduct={hasSingleCombinableOption ? false : isParentProduct}
								quantity={currentQty}
								isPurchaseView
								combinable={!hasSingleCombinableOption}
								selected={isSelected}
								maxQuantity={Math.min(remainingForThisItem, availableQty ?? Infinity)}
								onQuantityChange={(qty) => updateSkuQuantity(sku.sku, qty)}
								outOfStock={sku.sku !== currentPartNumber && isOutOfStock(marketInventory[sku.sku])}
							/>
						</Box>
					</Box>
				);
			})}
		</>
	);
};
